import type { ComponentType, ReactNode } from 'react';
import React, { Suspense, useCallback } from 'react';
import { Link } from '@helpcenter/analytics-components';
import { DropdownItem } from '@helpcenter/analytics-components/src/dropdown-item';
import { ScreenName, USER_TYPING_DEBOUNCE_TIME } from '@helpcenter/constants';
import ErrorBoundary from '@helpcenter/error-boundary/src/error-boundary';
import { getCloudId } from '@helpcenter/meta';
import ErrorScreen from 'assets/images/project-mapping-error.svg';
import { debounce, noop } from 'lodash';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, useLazyLoadQuery } from 'react-relay';
import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import EmptyState from '@atlaskit/empty-state/empty-state';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import Heading from '@atlaskit/heading/heading';
import Search from '@atlaskit/icon/core/search';
import { Box } from '@atlaskit/primitives/box';
import { Inline } from '@atlaskit/primitives/inline';
import { Stack } from '@atlaskit/primitives/stack';
import { Text } from '@atlaskit/primitives/text';
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import Textfield from '@atlaskit/textfield/text-field';
import { ContextWrapper as ContextualAnalyticsData } from '@atlassian/analytics-bridge/context-wrapper';
import { SCREEN } from '@atlassian/analytics-bridge/constants';
import { TRACK_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { FireScreenEventOnMount } from '@atlassian/help-center-common-util/analytics/fire-screen-event';
import { getIsMultiHCEnabled } from '@atlassian/help-center-common-util/env';
import { isFetchNetworkOrClientError } from '@atlassian/help-center-common-util/fetch';
import type { FetchError } from '@atlassian/help-center-common-util/fetch';
import { ExperienceSuccess } from '@atlassian/ufo/experience-success';
import { useUFOComponentExperience } from '@atlassian/ufo/use-component-experience';
import { useHelpCenterType } from '../../contexts/help-center-type-context';
import { useHelpCenterAri } from '../../hooks/use-help-center-ari';
import { AdminManagementLayout } from '../admin-management-layout';
import { SettingsBreadcrumbs } from '../settings-breadcrumbs';
import type { manageProjects_GetProjectsQuery } from './__generated__/manageProjects_GetProjectsQuery.graphql';
import { manageProjectsExperience } from './experiences';
import { ManageProjectsLayoutSkeleton } from './manage-projects-layout-skeleton';
import messages from './messages';
import { ProjectsList } from './projects-list';
import { SyncProjectsToggle } from './sync-projects-toggle';
import type { StatusTextMap } from './types';
import { ProjectsMappingStatus } from './types';

const PROJECT_MAPPING_SUPPORT_DOC_URL =
	'https://support.atlassian.com/jira-service-management-cloud/docs/link-service-projects-to-a-help-center/';

interface StaticContentProps {
	isReadOnlyView: boolean;
}
const StaticContent: React.FC<StaticContentProps> = ({ isReadOnlyView }: StaticContentProps) => {
	di(useHelpCenterType);
	const { formatMessage } = useIntl();
	const { isHelpHub } = useHelpCenterType();
	const isGalaxia = FeatureGates.checkGate('itsm-project-renaming');
	const helpCenterManageProjectsLink = (chunks: ReactNode) => (
		<Link
			href={PROJECT_MAPPING_SUPPORT_DOC_URL}
			actionSubjectId="manageProjectsLink"
			target="_blank"
			rel="noreferrer"
		>
			{chunks}
		</Link>
	);

	const pageTitle = isGalaxia
		? messages.manageProjectsPageTitleGalaxia
		: messages.manageProjectsPageTitle;

	let pageDescription;
	if (isReadOnlyView) {
		pageDescription = isGalaxia
			? messages.manageProjectsPageDescriptionForDefaultHCGalaxia
			: messages.manageProjectsPageDescriptionForDefaultHC;
	} else if (isHelpHub) {
		pageDescription = messages.manageProjectsPageDescriptionHelpHub;
	} else if (isGalaxia) {
		pageDescription = messages.manageProjectsPageDescriptionGalaxia;
	} else {
		pageDescription = messages.manageProjectsPageDescription;
	}

	return (
		<>
			<Heading size="large">{formatMessage(pageTitle)}</Heading>
			{isReadOnlyView ? (
				<Box>
					<Text>{formatMessage(pageDescription, { link: helpCenterManageProjectsLink })}</Text>
				</Box>
			) : (
				<Text>
					{isHelpHub
						? formatMessage(pageDescription)
						: formatMessage(pageDescription, { link: helpCenterManageProjectsLink })}
				</Text>
			)}
		</>
	);
};

const HelpCenterManageProjectsComponent: React.FC = () => {
	di(getIsMultiHCEnabled);
	const [helpCenterMappingStatus, setHelpCenterMappingStatus] =
		React.useState<ProjectsMappingStatus>(ProjectsMappingStatus.LINKED);
	const helpCenterAri = useHelpCenterAri();
	const data = useLazyLoadQuery<manageProjects_GetProjectsQuery>(
		graphql`
			query manageProjects_GetProjectsQuery(
				$cloudId: ID!
				$helpCenterId: ID!
				$helpCenterARI: ID!
				$helpCenterMappingStatus: JiraProjectsHelpCenterMappingStatus!
			) {
				jira {
					jiraProjectsMappedToHelpCenter(
						cloudId: $cloudId
						filter: {
							helpCenterId: $helpCenterId
							helpCenterMappingStatus: $helpCenterMappingStatus
							helpCenterARI: $helpCenterARI
						}
					) {
						...projectsListFragment
					}
				}
				helpCenter(cloudId: $cloudId) {
					helpCenterById(helpCenterAri: $helpCenterARI) @optIn(to: ["HelpCenterAggBeta"]) {
						...syncProjectsToggleFragment @dangerously_unaliased_fixme
						... on HelpCenter {
							slug @optIn(to: "HelpCenterSlugTest")
						}
						...settingsBreadcrumbsFragment @dangerously_unaliased_fixme
					}
				}
			}
		`,
		{
			helpCenterMappingStatus,
			helpCenterId: '',
			helpCenterARI: helpCenterAri,
			cloudId: getCloudId(),
		},
	);
	const { formatMessage } = useIntl();

	const [searchQuery, setSearchQuery] = React.useState<string>('');

	const onInputChange = (value: string): void => {
		setSearchQuery(value);
	};

	const debouncedOnChange = debounce(onInputChange, USER_TYPING_DEBOUNCE_TIME);

	const onTextFieldChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
		(event) => {
			const value = event.target.value;
			debouncedOnChange(value);
		},
		[debouncedOnChange],
	);

	const statusTextMap: StatusTextMap = {
		[ProjectsMappingStatus.LINKED]: FeatureGates.checkGate('itsm-project-renaming')
			? formatMessage(messages.linkedFilterLabelGalaxia)
			: formatMessage(messages.linkedFilterLabel),
		[ProjectsMappingStatus.UNLINKED]: FeatureGates.checkGate('itsm-project-renaming')
			? formatMessage(messages.unlinkedFilterLabelGalaxia)
			: formatMessage(messages.unlinkedFilterLabel),
	};

	if (!data?.jira?.jiraProjectsMappedToHelpCenter) {
		return null;
	}

	if (!data?.helpCenter?.helpCenterById) {
		return null;
	}

	const slug = data.helpCenter?.helpCenterById?.slug;
	const isDefaultHC = !slug;
	const isReadOnlyView = !getIsMultiHCEnabled() || isDefaultHC;

	return (
		<ContextualAnalyticsData sourceName={ScreenName.MANAGE_PROJECTS} sourceType={SCREEN}>
			<Stack space="space.300">
				<SettingsBreadcrumbs helpCenterData={data.helpCenter?.helpCenterById} />
				<StaticContent isReadOnlyView={isReadOnlyView} />
				{!isReadOnlyView && (
					<Box>
						<Inline alignBlock="center">
							<Text weight="medium" color="color.text.subtle">
								{FeatureGates.checkGate('itsm-project-renaming')
									? formatMessage(messages.manageProjectPageToggleForDefaultLinkingGalaxia)
									: formatMessage(messages.manageProjectPageToggleForDefaultLinking)}
							</Text>
							<SyncProjectsToggle syncProjectsToggleFragmentKey={data.helpCenter?.helpCenterById} />
						</Inline>
					</Box>
				)}
				<Box paddingBlock="space.200">
					<Inline>
						<Box xcss={searchInputStyles}>
							<Textfield
								name="search"
								autoComplete="off"
								isCompact
								elemAfterInput={
									<Box paddingInlineEnd="space.100">
										<Search size="small" label="search icon" spacing="spacious" />
									</Box>
								}
								defaultValue={searchQuery}
								onChange={onTextFieldChange}
								placeholder={
									FeatureGates.checkGate('itsm-project-renaming')
										? formatMessage(messages.searchProjectsPlaceholderGalaxia)
										: formatMessage(messages.searchProjectsPlaceholder)
								}
							/>
						</Box>
						{!isReadOnlyView && (
							<AnalyticsContext data={{ actionSubjectId: 'projectMappingMenu' }}>
								<Box xcss={dropdownContainerStyles}>
									<DropdownMenu trigger={statusTextMap[helpCenterMappingStatus]} shouldFitContainer>
										<DropdownItemGroup>
											<AnalyticsContext data={{ actionSubjectId: 'projectMappingLinkedMenuItem' }}>
												<DropdownItem
													onClick={() => {
														setHelpCenterMappingStatus(ProjectsMappingStatus.LINKED);
													}}
												>
													{statusTextMap[ProjectsMappingStatus.LINKED]}
												</DropdownItem>
											</AnalyticsContext>
											<AnalyticsContext
												data={{ actionSubjectId: 'projectMappingUnLinkedMenuItem' }}
											>
												<DropdownItem
													onClick={() => {
														setHelpCenterMappingStatus(ProjectsMappingStatus.UNLINKED);
													}}
												>
													{statusTextMap[ProjectsMappingStatus.UNLINKED]}
												</DropdownItem>
											</AnalyticsContext>
										</DropdownItemGroup>
									</DropdownMenu>
								</Box>
							</AnalyticsContext>
						)}
					</Inline>
				</Box>
				<ProjectsList
					isReadOnlyView={isReadOnlyView}
					projectsListFragmentKey={data?.jira?.jiraProjectsMappedToHelpCenter}
					selection={helpCenterMappingStatus}
					searchQuery={searchQuery}
				/>
				<ExperienceSuccess experience={manageProjectsExperience} />
				<FireScreenEventOnMount />
			</Stack>
		</ContextualAnalyticsData>
	);
};

export const HelpCenterManageProjects: ComponentType = () => {
	di(useHelpCenterAri, AdminManagementLayout, SyncProjectsToggle);
	useUFOComponentExperience(manageProjectsExperience);
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onError = (error: Error | FetchError) => {
		if (!isFetchNetworkOrClientError(error as FetchError)) {
			manageProjectsExperience
				.failure({
					metadata: {
						error: error?.message,
						statusCode: (error as FetchError)?.status,
					},
				})
				.then(noop)
				.catch(noop);
			//TODO: Add helpCenterMappingStatus
			createAnalyticsEvent({
				analyticsType: TRACK_EVENT_TYPE,
				action: 'fetch_failed',
				actionSubject: 'project',
				errorMessage: error?.message,
				errorStatus: (error as FetchError)?.status,
			}).fire();
		}
	};

	const renderError = () => {
		return (
			<EmptyState
				header={formatMessage(messages.errorTitle)}
				description={formatMessage(messages.errorDescription)}
				imageUrl={ErrorScreen}
			/>
		);
	};
	return (
		<AdminManagementLayout featureName="Helpcenter manage projects">
			<ErrorBoundary
				packageName={ScreenName.MANAGE_PROJECTS}
				id={'help-center-manage-projects'}
				renderError={renderError}
				onError={onError}
			>
				<Suspense fallback={<ManageProjectsLayoutSkeleton />}>
					<HelpCenterManageProjectsComponent />
				</Suspense>
			</ErrorBoundary>
		</AdminManagementLayout>
	);
};

const searchInputStyles = xcss({
	width: '288px',
	marginRight: 'space.200',
});

const dropdownContainerStyles = xcss({
	zIndex: 'modal',
});
